import { CLIENT_HEADER, WEB_CLIENT, chatStreamEventSchema, contract } from '@logistics/contracts';
import type { ChatStreamEvent, QuestionOption } from '@logistics/contracts';
import { BASE_URL, notifyUnauthorized } from '../client';
import { ApiFailure } from '@/lib/result';
import { readSse } from '@/lib/sse';

export interface ChatRequest {
  readonly conversationId?: string | undefined;
  readonly text: string;
  readonly scope: readonly QuestionOption[];
  readonly attachmentIds?: readonly string[] | undefined;
}

/**
 * POST /chat/stream with a plain `fetch`, since the typed client cannot read a
 * stream. It therefore does by hand what the client does for every other call:
 * sends the cookie and the CSRF header, and reports a 401. A refusal before
 * the stream opens (400, 404) is ordinary JSON and is thrown as an ApiFailure.
 * An event that does not match the contract is dropped rather than trusted.
 */
export async function streamChat(
  request: ChatRequest,
  signal: AbortSignal,
  onEvent: (event: ChatStreamEvent) => void,
): Promise<void> {
  const response = await fetch(`${BASE_URL}${contract.chat.stream.path}`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      accept: 'text/event-stream',
      [CLIENT_HEADER]: WEB_CLIENT,
    },
    body: JSON.stringify({
      text: request.text,
      scope: request.scope,
      ...(request.conversationId === undefined ? {} : { conversationId: request.conversationId }),
      ...(request.attachmentIds === undefined || request.attachmentIds.length === 0
        ? {}
        : { attachmentIds: request.attachmentIds }),
    }),
    signal,
  });

  if (response.status === 401) notifyUnauthorized();
  const type = response.headers.get('content-type') ?? '';
  if (!response.ok || !type.includes('text/event-stream') || response.body === null) {
    throw new ApiFailure(response.status, await errorMessage(response));
  }

  await readSse(response.body, (data) => {
    let json: unknown;
    try {
      json = JSON.parse(data);
    } catch {
      return;
    }
    const parsed = chatStreamEventSchema.safeParse(json);
    if (parsed.success) onEvent(parsed.data);
  });
}

async function errorMessage(response: Response): Promise<string | undefined> {
  try {
    const body = (await response.json()) as { message?: unknown };
    return typeof body.message === 'string' ? body.message : undefined;
  } catch {
    return undefined;
  }
}
